/**
 * Super-admin demo links API contract (SCH-1788)
 *
 * The dashboard's "App Demo" screen (`/admin/demo`) manages the expiring,
 * revocable prospect links that open `demo.schooljourney.com` for a prospect.
 * They used to be managed on the demo host's presenter page; that page is shared
 * on screen in client presentations, so link management moved here.
 *
 * Mounted at `/v1/admin/demo`. Authentication is the standard dashboard one,
 * `checkAuthMiddleware` (a Clerk session plus the `x-school-id` active-school
 * header the dashboard always sends), exactly as `/v1/admin/blog`; the JWT's
 * `isSuperAdmin` is then checked as the first statement of each use-case.
 *
 *   | Route                                     | Body                    | Answer                            |
 *   |-------------------------------------------|-------------------------|-----------------------------------|
 *   | GET  /v1/admin/demo/personas              | —                       | {@link AdminDemoPersonasResponse} |
 *   | GET  /v1/admin/demo/links                 | —                       | `DemoLinkListResponse`            |
 *   | POST /v1/admin/demo/links                 | `DemoLinkCreateRequest` | `DemoLinkCreateResponse` (201)    |
 *   | POST /v1/admin/demo/links/:linkId/revoke  | —                       | `DemoLinkRevokeResponse`          |
 *
 * THE LINK SHAPES ARE THE INTERNAL ONES (`internal/demo.ts`), unchanged: these
 * routes run the same link use-cases as `/internal/demo/links*`, with the
 * signed-in super-admin as the presenter. `activeForPresenter` in the list
 * counts the caller's own active links.
 *
 * The create answer's `token` is the plaintext prospect token, returned by that
 * call and never again. Build the prospect URL with {@link buildDemoProspectUrl}
 * and keep it in component state only: never a log, a URL, a persisted cache
 * or an analytics property.
 *
 * Refusals use the demo envelope `{ error: { type, message, details? } }` with
 * `type` from `DEMO_REFUSAL_CODES` (`TARGET_REFUSED`, `TEACHER_OPT_IN_REQUIRED`,
 * `ACTIVE_LINK_LIMIT`, `LINK_NOT_FOUND`, `INVALID_REQUEST`); a caller who is not
 * a super-admin gets `FORBIDDEN` (403).
 */

import type { z } from 'zod'
import { DEMO_HOST_PRODUCTION_ORIGIN, DemoPersonasResponseSchema } from '../internal/demo'

/**
 * GET /v1/admin/demo/personas — every persona a link may carry: the demo
 * school's members who pass the KTD9 predicate. The internal answer's `link`
 * field is always `null` for a presenter, so it is left out here.
 */
export const AdminDemoPersonasResponseSchema = DemoPersonasResponseSchema.omit({ link: true })
export type AdminDemoPersonasResponse = z.infer<typeof AdminDemoPersonasResponseSchema>

/** The demo host's landing path. The token rides in the fragment, which never reaches a server (KTD10). */
const DEMO_PROSPECT_LANDING_PATH = '/go'

/** The URL a prospect opens: `https://demo.schooljourney.com/go#<token>`. */
export function buildDemoProspectUrl(token: string): string {
  return `${DEMO_HOST_PRODUCTION_ORIGIN}${DEMO_PROSPECT_LANDING_PATH}#${token}`
}
